import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle scroll for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8 mx-auto">
              <button onClick={() => scrollToSection('home')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'}`}>Home</button>
              <button onClick={() => scrollToSection('problema')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'}`}>Il Problema</button>
              <button onClick={() => scrollToSection('soluzione')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'}`}>Soluzione</button>
              <button onClick={() => scrollToSection('metodo')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'}`}>Metodo E-LUME</button>
              <button onClick={() => scrollToSection('servizi')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'}`}>Servizi</button>
              <Link to="/collabora" className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'}`}>Collabora</Link>
              <button onClick={() => scrollToSection('contatti')} className="bg-teal-500 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-600 transition-colors whitespace-nowrap">Contattaci</button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden">
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-500 py-2 whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('problema')} className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-500 py-2 whitespace-nowrap">Il Problema</button>
              <button onClick={() => scrollToSection('soluzione')} className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-500 py-2 whitespace-nowrap">Soluzione</button>
              <button onClick={() => scrollToSection('metodo')} className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-500 py-2 whitespace-nowrap">Metodo E-LUME</button>
              <button onClick={() => scrollToSection('servizi')} className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-500 py-2 whitespace-nowrap">Servizi</button>
              <Link to="/collabora" className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-500 py-2 whitespace-nowrap">Collabora</Link>
              <button onClick={() => scrollToSection('contatti')} className="block w-full text-left bg-teal-500 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-600 transition-colors whitespace-nowrap">Contattaci</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/3b29c0bc6c2a66ff44df641ed2a6e098.jpg" 
            alt="Energy Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="flex flex-col items-center text-center">
            <div className="text-white max-w-4xl">
              <div className="mb-8">
                <img 
                  src="https://static.readdy.ai/image/4153abc494094421aaf0d0211ae45ade/759e4e633c72ff883f50db1e3ba0bc18.png" 
                  alt="E-LUME Logo" 
                  className="h-48 w-auto mb-4 mx-auto"
                />
                <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-wider">
                  ENERGY MAKER
                </h1>
                <div className="text-base font-semibold text-teal-400 tracking-wide mb-6">
                  Smart Energy & Sustainability Solutions
                </div>
                
                {/* Contact Numbers */}
                <div className="flex flex-wrap justify-center gap-6 mb-8">
                  <a href="tel:+393881415515" className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full hover:bg-white/20 transition-all">
                    <i className="ri-phone-line text-xl text-teal-400"></i>
                    <span className="text-white font-semibold">+39 388 141 5515</span>
                  </a>
                  <a href="mailto:info@e-lume.com" className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full hover:bg-white/20 transition-all">
                    <i className="ri-mail-line text-xl text-teal-400"></i>
                    <span className="text-white font-semibold">info@e-lume.com</span>
                  </a>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-extrabold leading-tight mb-6">
                Soluzioni <span className="text-teal-400">intelligenti</span> per l'energia e la <span className="italic">sostenibilità</span>
              </h2>
              <p className="text-xl text-gray-200 leading-relaxed mb-8 max-w-3xl mx-auto">
                Con il Metodo E-LUME illuminiamo le tue scelte energetiche e trasformiamo l'energia da costo a opportunità.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <button onClick={() => scrollToSection('metodo')} className="bg-black text-white px-10 py-4 rounded-2xl text-base font-semibold hover:bg-gray-900 transition-all hover:scale-105 whitespace-nowrap">
                  Scopri il Metodo E-LUME
                </button>
                <button onClick={() => scrollToSection('contatti')} className="border-2 border-teal-400 text-white px-10 py-4 rounded-2xl text-base font-semibold hover:bg-teal-400/20 transition-all whitespace-nowrap">
                  Richiedi una consulenza
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problema" className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div className="w-2 h-2 bg-black rounded-full"></div>
              <span className="text-sm font-bold uppercase tracking-wider text-gray-600">IL PROBLEMA</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-blue-600 leading-tight">
              L'energia oggi è confusa,<br />costosa e difficile da governare
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-10 rounded-3xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-file-list-3-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Bollette poco chiare</h3>
              <p className="text-gray-600 leading-relaxed">Voci incomprensibili, costi nascosti e tariffe che cambiano senza preavviso.</p>
            </div>

            <div className="bg-white p-10 rounded-3xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-question-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Offerte incomprensibili</h3>
              <p className="text-gray-600 leading-relaxed">Promesse che non corrispondono alla realtà e contratti pieni di clausole nascoste.</p>
            </div>

            <div className="bg-white p-10 rounded-3xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-bar-chart-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Decisioni senza dati reali</h3>
              <p className="text-gray-600 leading-relaxed">Scelte energetiche basate su sensazioni invece che su analisi concrete.</p>
            </div>

            <div className="bg-white p-10 rounded-3xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-money-euro-circle-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Costi troppo alti</h3>
              <p className="text-gray-600 leading-relaxed">Famiglie, imprese e condomìni pagano troppo senza sapere perché.</p>
            </div>
          </div>

          <p className="text-center text-xl italic text-gray-700 max-w-3xl mx-auto">
            Energy Maker nasce per risolvere questo problema: portare chiarezza dove oggi c'è complessità.
          </p>
        </div>
      </section>

      {/* Solution Section */}
      <section id="soluzione" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 mb-4">
                <div className="w-2 h-2 bg-black rounded-full"></div>
                <span className="text-sm font-bold uppercase tracking-wider text-gray-600">LA SOLUZIONE</span>
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Non vendiamo energia.<br />
                <span className="bg-gradient-to-r from-teal-500 to-blue-500 bg-clip-text text-transparent">
                  Costruiamo scelte energetiche intelligenti.
                </span>
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Energy Maker è il brand che aiuta persone e organizzazioni a produrre, scegliere e usare l'energia in modo consapevole, efficiente e sostenibile.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Lo facciamo attraverso il <span className="bg-yellow-100 px-2 py-1 font-semibold">Metodo E-LUME</span>: un approccio strutturato, umano e trasparente che mette finalmente il cliente al centro.
              </p>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20energy%20consultant%20analyzing%20renewable%20energy%20data%20on%20modern%20tablet%20device%20with%20large%20solar%20panel%20installation%20visible%20in%20bright%20sunny%20background%2C%20clean%20contemporary%20business%20photography%2C%20person%20in%20professional%20attire%20reviewing%20energy%20efficiency%20reports%20and%20sustainability%20metrics%2C%20natural%20daylight%2C%20sharp%20focus%2C%20high%20quality%20corporate%20photography&width=800&height=600&seq=elume-solution-v1&orientation=landscape" 
                  alt="Energy Solution" 
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-500/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Method E-LUME Section */}
      <section id="metodo" className="py-24 bg-gradient-radial from-teal-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-lightbulb-line text-4xl text-white"></i>
            </div>
            <h2 className="text-6xl font-extrabold text-gray-900 mb-4">Il Metodo E-LUME</h2>
            <p className="text-2xl italic text-gray-600">La luce che guida le tue scelte energetiche</p>
          </div>

          <div className="max-w-5xl mx-auto mb-12">
            <p className="text-lg text-gray-700 leading-relaxed text-center mb-8">
              E-LUME è il metodo sviluppato da oltre 10 anni di esperienza nel settore energetico. Unisce analisi tecnica, consulenza strategica ed educazione energetica per aiutarti a:
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mb-12">
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 w-full md:w-72 relative overflow-hidden">
              <div className="absolute top-6 left-6 text-8xl font-bold text-gray-100">1</div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-search-eye-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Capire davvero</h3>
                <p className="text-gray-600 leading-relaxed">Cosa stai pagando e perché, con analisi dettagliate e trasparenti.</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 w-full md:w-72 relative overflow-hidden">
              <div className="absolute top-6 left-6 text-8xl font-bold text-gray-100">2</div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-settings-3-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Ottimizzare</h3>
                <p className="text-gray-600 leading-relaxed">Consumi e contratti per ridurre gli sprechi e massimizzare l'efficienza.</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 w-full md:w-72 relative overflow-hidden">
              <div className="absolute top-6 left-6 text-8xl font-bold text-gray-100">3</div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-leaf-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Scegliere sostenibile</h3>
                <p className="text-gray-600 leading-relaxed">Soluzioni convenienti che rispettano l'ambiente e il tuo budget.</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 w-full md:w-72 relative overflow-hidden">
              <div className="absolute top-6 left-6 text-8xl font-bold text-gray-100">4</div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-blue-500 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-line-chart-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Decidere con dati</h3>
                <p className="text-gray-600 leading-relaxed">Non su promesse, ma su analisi concrete e verificabili.</p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-2xl font-semibold text-gray-900 mb-8">
              👉 Con E-LUME l'energia diventa finalmente chiara.
            </p>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">A chi ci rivolgiamo</h2>
            <p className="text-xl text-gray-600">A chi vuole riprendere il controllo della propria energia</p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-green-50 rounded-3xl p-12 hover:scale-105 transition-transform">
              <div className="w-20 h-20 mb-6">
                <i className="ri-home-heart-line text-7xl text-green-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Famiglie e privati</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-2 mt-1"></i>
                  <span className="text-gray-700">Capire la tua bolletta</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-2 mt-1"></i>
                  <span className="text-gray-700">Ridurre i consumi</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-2 mt-1"></i>
                  <span className="text-gray-700">Pagare il giusto</span>
                </li>
              </ul>
            </div>

            <div className="bg-teal-50 rounded-3xl p-12 hover:scale-105 transition-transform md:scale-110">
              <div className="w-20 h-20 mb-6">
                <i className="ri-building-line text-7xl text-teal-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">PMI e Aziende</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-2 mt-1"></i>
                  <span className="text-gray-700">Ottimizzare i costi energetici</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-2 mt-1"></i>
                  <span className="text-gray-700">Migliorare l'efficienza</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-2 mt-1"></i>
                  <span className="text-gray-700">Aumentare la sostenibilità</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-3xl p-12 hover:scale-105 transition-transform">
              <div className="w-20 h-20 mb-6">
                <i className="ri-community-line text-7xl text-blue-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Condomìni e Amministratori</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-2 mt-1"></i>
                  <span className="text-gray-700">Gestione trasparente</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-2 mt-1"></i>
                  <span className="text-gray-700">Efficienza energetica</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-500 text-xl mr-2 mt-1"></i>
                  <span className="text-gray-700">Senza conflitti</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servizi" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div className="w-2 h-2 bg-black rounded-full"></div>
              <span className="text-sm font-bold uppercase tracking-wider text-gray-600">SERVIZI</span>
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">I servizi Energy Maker con il Metodo E-LUME</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* MONITORAGGIO - Servizio in evidenza */}
            <div className="md:col-span-2 bg-gradient-to-br from-green-50 via-green-100 to-emerald-100 border-4 border-green-500 rounded-3xl p-12 hover:shadow-2xl transition-all hover:-translate-y-1 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-green-500 text-white px-6 py-2 rounded-bl-3xl font-bold text-sm shadow-lg">
                ⭐ IL NOSTRO CAVALLO DI BATTAGLIA
              </div>
              <div className="absolute bottom-4 right-4 opacity-10">
                <i className="ri-line-chart-line text-9xl text-green-600"></i>
              </div>
              <div className="relative z-10 mt-8">
                <div className="inline-block bg-black text-white px-4 py-2 rounded-lg text-sm font-bold mb-4">
                  E-LUME |
                </div>
                <h3 className="text-4xl font-extrabold text-gray-900 mb-4 flex items-center">
                  <i className="ri-dashboard-line text-green-600 mr-3"></i>
                  MONITORAGGIO
                </h3>
                <p className="text-xl text-gray-800 leading-relaxed mb-6 font-medium">
                  Hai il controllo totale dei tuoi consumi energetici, giorno per giorno, in tempo reale.
                </p>
                <div className="bg-white/80 rounded-2xl p-6 mb-6">
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                        <i className="ri-check-line text-white text-lg"></i>
                      </div>
                      <div>
                        <strong className="text-gray-900">Controllo giornaliero dei consumi</strong>
                        <p className="text-gray-700">Monitora in tempo reale quanto stai consumando ogni giorno</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                        <i className="ri-check-line text-white text-lg"></i>
                      </div>
                      <div>
                        <strong className="text-gray-900">Analisi per elettrodomestico</strong>
                        <p className="text-gray-700">Scopri esattamente quanto consuma ogni dispositivo e punto di prelievo</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                        <i className="ri-check-line text-white text-lg"></i>
                      </div>
                      <div>
                        <strong className="text-gray-900">Spesa giornaliera in tempo reale</strong>
                        <p className="text-gray-700">Sai sempre quanto stai spendendo, senza sorprese</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                        <i className="ri-check-line text-white text-lg"></i>
                      </div>
                      <div>
                        <strong className="text-gray-900">Bolletta prevedibile</strong>
                        <p className="text-gray-700">La bolletta a fine mese non sarà più una sorpresa: sai già quanto pagherai</p>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="bg-green-500 text-white rounded-2xl p-6 text-center">
                  <p className="text-lg font-bold mb-2">💡 Finalmente hai il controllo totale della tua energia!</p>
                  <p className="text-sm">Niente più bollette inaspettate. Niente più sprechi nascosti.</p>
                </div>
                <button onClick={() => scrollToSection('contatti')} className="mt-6 inline-flex items-center text-green-700 font-bold text-lg hover:text-green-800 cursor-pointer whitespace-nowrap">
                  Scopri di più <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>

            <div className="md:col-span-2 bg-gradient-to-br from-teal-50 to-teal-100 border-2 border-teal-500 rounded-3xl p-10 hover:shadow-xl transition-shadow relative overflow-hidden">
              <div className="absolute bottom-4 right-4 opacity-10">
                <i className="ri-file-list-3-line text-9xl text-teal-600"></i>
              </div>
              <div className="relative z-10">
                <div className="inline-block bg-black text-white px-4 py-2 rounded-lg text-sm font-bold mb-4">
                  E-LUME |
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Bolletta Sotto Controllo</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Analisi bollette luce e gas, verifica contratti, strategie di risparmio personalizzate per ridurre i costi e ottimizzare i consumi.
                </p>
                <button onClick={() => scrollToSection('contatti')} className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 cursor-pointer whitespace-nowrap">
                  Scopri di più <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-500 rounded-3xl p-10 hover:shadow-xl transition-shadow relative overflow-hidden">
              <div className="absolute bottom-4 right-4 opacity-10">
                <i className="ri-sun-line text-9xl text-green-600"></i>
              </div>
              <div className="relative z-10">
                <div className="inline-block bg-black text-white px-4 py-2 rounded-lg text-sm font-bold mb-4">
                  E-LUME |
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Efficienza energetica</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Fotovoltaico, pompe di calore, ottimizzazione dei consumi per un futuro più sostenibile.
                </p>
                <button onClick={() => scrollToSection('contatti')} className="inline-flex items-center text-green-600 font-semibold hover:text-green-700 cursor-pointer whitespace-nowrap">
                  Scopri di più <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-500 rounded-3xl p-10 hover:shadow-xl transition-shadow relative overflow-hidden">
              <div className="absolute bottom-4 right-4 opacity-10">
                <i className="ri-building-2-line text-9xl text-blue-600"></i>
              </div>
              <div className="relative z-10">
                <div className="inline-block bg-black text-white px-4 py-2 rounded-lg text-sm font-bold mb-4">
                  E-LUME |
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Condomini</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Consulenza energetica dedicata ad amministratori e stabili per una gestione efficiente.
                </p>
                <button onClick={() => scrollToSection('contatti')} className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 cursor-pointer whitespace-nowrap">
                  Scopri di più <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>

            <div className="md:col-span-2 bg-gradient-to-br from-teal-50 to-blue-100 border-2 border-teal-500 rounded-3xl p-10 hover:shadow-xl transition-shadow relative overflow-hidden">
              <div className="absolute bottom-4 right-4 opacity-10">
                <i className="ri-graduation-cap-line text-9xl text-teal-600"></i>
              </div>
              <div className="relative z-10">
                <div className="inline-block bg-black text-white px-4 py-2 rounded-lg text-sm font-bold mb-4">
                  E-LUME |
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Formazione & Academy</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Percorsi educativi, corsi e materiali per diventare consumatori consapevoli e gestire l'energia in autonomia.
                </p>
                <button onClick={() => scrollToSection('contatti')} className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 cursor-pointer whitespace-nowrap">
                  Scopri di più <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=breathtaking%20futuristic%20sustainable%20smart%20city%20aerial%20view%20with%20extensive%20solar%20panel%20arrays%20and%20wind%20turbines%2C%20modern%20eco-friendly%20architecture%20integrated%20with%20renewable%20energy%20systems%2C%20bright%20hopeful%20atmosphere%20with%20blue%20sky%20and%20sunlight%2C%20clean%20green%20technology%20infrastructure%2C%20professional%20architectural%20photography%20showing%20sustainable%20urban%20development%2C%20ultra%20sharp%20and%20detailed%2C%20vibrant%20colors&width=1920&height=1080&seq=elume-vision-v1&orientation=landscape" 
            alt="Vision Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="mb-6">
            <span className="text-sm font-bold uppercase tracking-widest text-white">LA NOSTRA VISION</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-12">La nostra visione</h2>
          
          <div className="grid md:grid-cols-2 gap-16 text-left">
            <p className="text-xl text-white leading-relaxed">
              Immaginiamo un futuro in cui ogni persona e ogni impresa possa diventare un Energy Maker consapevole, capace di gestire l'energia in modo intelligente, autonomo e sostenibile.
            </p>
            <p className="text-xl text-white leading-relaxed">
              Un futuro in cui l'energia non è più un problema da subire, ma una risorsa da governare.
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="w-full max-w-md mx-auto">
                <div className="relative">
                  <img 
                    src="https://static.readdy.ai/image/4153abc494094421aaf0d0211ae45ade/c47e44136a22edb1b4e3f2186f3c0ab6.png" 
                    alt="Energy Consultant" 
                    className="w-96 h-96 rounded-full object-cover object-top border-8 border-teal-500 shadow-2xl mx-auto"
                  />
                  <div className="absolute bottom-8 right-8 bg-teal-500 text-white px-6 py-3 rounded-full font-bold shadow-lg whitespace-nowrap">
                    10+ anni esperienza
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="inline-flex items-center space-x-2 mb-4">
                <div className="w-2 h-2 bg-black rounded-full"></div>
                <span className="text-sm font-bold uppercase tracking-wider text-gray-600">CHI C'È DIETRO</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Un'esperienza reale al tuo servizio</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Energy Maker nasce dall'esperienza di un consulente energetico con oltre 10 anni di attività nel settore, una visione chiara della sostenibilità e un obiettivo preciso: portare luce in un mercato complesso.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-medal-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Competenza tecnica</h4>
                    <p className="text-gray-600">Anni di esperienza nel settore energetico</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-heart-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Approccio umano</h4>
                    <p className="text-gray-600">Ascolto, empatia e soluzioni personalizzate</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-eye-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Totale trasparenza</h4>
                    <p className="text-gray-600">Chiarezza in ogni fase della consulenza</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gradient-to-br from-teal-500 via-teal-600 to-blue-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4">I nostri numeri parlano chiaro</h2>
            <p className="text-xl text-white/90">Risultati concreti costruiti in anni di esperienza</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-12 hover:bg-white/20 transition-all hover:scale-105">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-time-line text-4xl text-teal-600"></i>
                </div>
                <div className="text-7xl font-extrabold text-white mb-4">10</div>
                <div className="text-2xl font-semibold text-white mb-2">Anni di Esperienza</div>
                <p className="text-white/80">Nel settore energetico</p>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-12 hover:bg-white/20 transition-all hover:scale-105">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-group-line text-4xl text-teal-600"></i>
                </div>
                <div className="text-7xl font-extrabold text-white mb-4">4500</div>
                <div className="text-2xl font-semibold text-white mb-2">Clienti Soddisfatti</div>
                <p className="text-white/80">Che hanno scelto E-LUME</p>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-12 hover:bg-white/20 transition-all hover:scale-105">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-links-line text-4xl text-teal-600"></i>
                </div>
                <div className="text-7xl font-extrabold text-white mb-4">53</div>
                <div className="text-2xl font-semibold text-white mb-2">Affiliazioni Attive</div>
                <p className="text-white/80">Partner che credono in noi</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <p className="text-2xl text-white font-semibold">
              💡 Unisciti a migliaia di clienti che hanno già scelto la trasparenza
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="contatti" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-500 via-teal-600 to-blue-600"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-extrabold text-white mb-6">
            Sei pronto a diventare un Energy Maker?
          </h2>
          <p className="text-2xl text-white/90 mb-12 leading-relaxed">
            Scopri come il Metodo E-LUME può aiutarti a risparmiare, scegliere meglio e usare l'energia in modo intelligente.
          </p>
          
          <div className="flex flex-wrap justify-center gap-6 mb-16">
            <button onClick={() => scrollToSection('metodo')} className="bg-white text-teal-700 px-12 py-5 rounded-2xl text-lg font-bold hover:bg-gray-100 transition-all shadow-2xl hover:scale-105 whitespace-nowrap">
              Scopri il Metodo E-LUME
            </button>
            <button onClick={() => {
              const form = document.getElementById('contact-form');
              if (form) form.scrollIntoView({ behavior: 'smooth' });
            }} className="border-2 border-white text-white px-12 py-5 rounded-2xl text-lg font-bold hover:bg-white/10 transition-all whitespace-nowrap">
              Prenota una consulenza
            </button>
          </div>

          {/* Contact Form */}
          <div id="contact-form" className="bg-white rounded-3xl p-10 max-w-2xl mx-auto shadow-2xl">
            <h3 className="text-3xl font-bold text-gray-900 mb-2">Richiedi una consulenza o collabora con noi</h3>
            <p className="text-gray-600 mb-8">Compila il form e ti ricontatteremo al più presto</p>
            
            <div className="w-full" style={{ minHeight: '492px' }}>
              <iframe
                src="https://link.delera.co/widget/form/lCDQTdKOE4mMKgKIuSYT"
                style={{ width: '100%', height: '100%', minHeight: '492px', border: 'none', borderRadius: '3px' }}
                id="inline-lCDQTdKOE4mMKgKIuSYT" 
                data-layout="{'id':'INLINE'}"
                data-trigger-type="alwaysShow"
                data-trigger-value=""
                data-activation-type="alwaysActivated"
                data-activation-value=""
                data-deactivation-type="neverDeactivate"
                data-deactivation-value=""
                data-form-name="Form E-lume.org"
                data-height="492"
                data-layout-iframe-id="inline-lCDQTdKOE4mMKgKIuSYT"
                data-form-id="lCDQTdKOE4mMKgKIuSYT"
                title="Form E-lume.org"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img src="https://static.readdy.ai/image/4153abc494094421aaf0d0211ae45ade/b65f5728d87d835b00ff4aad46a33f84.png" alt="Energy Maker" className="h-10 w-auto" />
                <div className="font-bold text-xl">ENERGY MAKER</div>
              </div>
              <p className="text-gray-400 text-sm mb-6">Soluzioni intelligenti per l'energia e la sostenibilità</p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 border-2 border-gray-600 rounded-full flex items-center justify-center hover:border-teal-400 transition-colors">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border-2 border-gray-600 rounded-full flex items-center justify-center hover:border-teal-400 transition-colors">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border-2 border-gray-600 rounded-full flex items-center justify-center hover:border-teal-400 transition-colors">
                  <i className="ri-instagram-line text-lg"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-4">Servizi</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('servizi')} className="text-gray-300 hover:text-teal-400 transition-colors text-sm cursor-pointer whitespace-nowrap">Bolletta Sotto Controllo</button></li>
                <li><button onClick={() => scrollToSection('servizi')} className="text-gray-300 hover:text-teal-400 transition-colors text-sm cursor-pointer whitespace-nowrap">Efficienza energetica</button></li>
                <li><button onClick={() => scrollToSection('servizi')} className="text-gray-300 hover:text-teal-400 transition-colors text-sm cursor-pointer whitespace-nowrap">Condomini</button></li>
                <li><button onClick={() => scrollToSection('servizi')} className="text-gray-300 hover:text-teal-400 transition-colors text-sm cursor-pointer whitespace-nowrap">Formazione & Academy</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-4">Azienda</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('metodo')} className="text-gray-300 hover:text-teal-400 transition-colors text-sm cursor-pointer whitespace-nowrap">Metodo E-LUME</button></li>
                <li><button onClick={() => scrollToSection('soluzione')} className="text-gray-300 hover:text-teal-400 transition-colors text-sm cursor-pointer whitespace-nowrap">Chi siamo</button></li>
                <li><Link to="/collabora" className="text-gray-300 hover:text-teal-400 transition-colors text-sm whitespace-nowrap">Collabora con noi</Link></li>
                <li><button onClick={() => scrollToSection('contatti')} className="text-gray-300 hover:text-teal-400 transition-colors text-sm cursor-pointer whitespace-nowrap">Contatti</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-4">Contatti</h4>
              <ul className="space-y-3 text-sm text-gray-300">
                <li className="flex items-start">
                  <i className="ri-mail-line mr-2 mt-1"></i>
                  <span>info@e-lume.com</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-phone-line mr-2 mt-1"></i>
                  <span>+39 388 141 5515</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-map-pin-line mr-2 mt-1"></i>
                  <span>Italia</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2025 Energy Maker. Tutti i diritti riservati.
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
              <a href="#" className="hover:text-teal-400 transition-colors whitespace-nowrap">Privacy Policy</a>
              <a href="#" className="hover:text-teal-400 transition-colors whitespace-nowrap">Cookie Policy</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors whitespace-nowrap">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}